import { useMemo, useState, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { Col, Row } from './Flexbox';
import Text from './Text';
import Dropdown from './Dropdown';
import {MenuItem} from "@mui/material";
import { monthMap } from '../utils/MonthMaps';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';

const quadrantPlugin = {
    id: 'backgroundQuadrant',

    beforeDatasetsDraw(chart, args, options) {
        const {ctx, chartArea: {left, top, right, bottom}, scales} = chart;
        const y = scales.y;

        const yThreshold = y.getPixelForValue(options.yValue);

        //Make sure the red area is inside the limits ()
        const rectBottom = Math.max(Math.min(yThreshold, bottom), top);
        const rectTop = top;
        const rectHeight = rectBottom - rectTop;

        //Draw the background if there's an actual height
        if (rectHeight > 0) {
            ctx.save();
            ctx.fillStyle = options.color;
            ctx.fillRect(left, rectTop, right - left, rectHeight);
            ctx.restore();
        }
    }
}

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, quadrantPlugin);

const hexToRgba = (hex, alpha = 1) => {
    hex = hex.replace(/^#/, '');
    if(hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
    }

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const PlantRecordLineChart = (props) => {

    const {theme, data, months, onAreaChange, selectedArea, onPercentageChange} = props;

    const chartRef = useRef(null);
    const graphRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [selectedLegend, setSelectedLegend] = useState('2');

    const individualMax = 14400;
    const areas = ['FLEXO', 'OFFSET', 'OTROS'];

    const baseColor = (area) => {
        const colorMap = {
            FLEXO: theme.Magenta,
            OFFSET: theme.Yellow,
            OTROS: theme.Cyan
        };
        return colorMap[area] || theme.Gray;
    }

    const sortedMonths = useMemo(() => {
        if (!months || months.length === 0) return [];

        return [...months].sort((a, b) => {
            const [nameA, yearA] = a.toLowerCase().split(' ');
            const [nameB, yearB] = b.toLowerCase().split(' ');

            return (yearA - yearB) || (monthMap[nameA] - monthMap[nameB]);
        });
    }, [months]);

    const chartPoints = useMemo(() => {
        const points = [];
        const selectedIndex = parseInt(selectedLegend, 10);
        const turnsCount = selectedIndex + 1;
        const selectedCapacity = turnsCount * individualMax;    //This is the capacity of a month

        if (selectedCapacity === 0) {
            return sortedMonths.map(() => 0);
        }

        for (const month of sortedMonths) {
            const monthData = data.values[month];

            if(!monthData) {
                points.push(0); //Save a zero if there's no data for the current month
                continue;
            }

            let sumTurns = 0;
            if (selectedIndex === 0) {
                sumTurns = monthData.valores[0];
            } else if (selectedIndex === 1) {
                sumTurns = monthData.valores[0] + monthData.valores[1];
            } else {
                sumTurns = monthData.valores[0] + monthData.valores[1] + monthData.valores[2];
            }

            const percentage = Math.round((sumTurns / selectedCapacity) * 100);
            points.push(percentage);
        }
        return points;
    }, [data.values, sortedMonths, selectedLegend])

    const handleSelectChange = (value) => {
        if(onAreaChange) {
            onAreaChange(data.nombre, value)
        }
    }

    const handleLegendChange = (value) => {
        setSelectedLegend(value);
    }

     const handleOutsideClick = (e) => {
        if (graphRef.current && graphRef.current.contains(e.target)) {  //Avoid closing it if clicked inside
            return; 
        }

        if (e.target.closest('.custom-legend')) {
            return;
        }

        if (e.target.closest('[role="listbox"]')) { //Checks if the click is inside this MUI element to avoid closing it
            return; 
        }

        setVisible(false);
    }

    useEffect(() => { 
        document.addEventListener("mousedown", handleOutsideClick);    //Checks for clicks out of the charts
        return () => document.removeEventListener("mousedown", handleOutsideClick); //Deletes the event 
    }, []);

    const averagePercent = useMemo(() => {
        if (chartPoints.length === 0) {
            return 0;
        }

        const average = chartPoints.reduce((sum, val) => sum + val, 0) / chartPoints.length;
        return Math.round(average);
    }, [chartPoints])

    useEffect(() => {
        if (onPercentageChange) {
            onPercentageChange(data.nombre, averagePercent)
        }
    }, [data.nombre, onPercentageChange, averagePercent])

    const chartConfig = useMemo(() => {
        const color = baseColor(data.area)

        return {
            data: {
                labels: sortedMonths.map(month => {
                    const monthName = month.split(' ')[0];
                    return monthName.charAt(0).toUpperCase() + monthName.slice(1);  //Format the month's name (e.g. Octubre)
                }),
                datasets: [{
                    label: data.nombre,
                    data: chartPoints,
                    borderColor: hexToRgba(color, 0.3),
                    backgroundColor: color,
                    pointStyle: true,
                    pointRadius: 7,
                    pointHoverRadius: 9,
                    pointBackgroundColor: hexToRgba(color, 0.4),
                    pointBorderColor: color,
                    pointBorderWidth: 1,
                    fill: false,
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                onClick: () => setVisible(true),
                scales: {
                    y: {
                        beginAtZero: true,
                        position: 'right',
                        title: {
                            display: false,
                        },
                        ticks: {
                            callback: (value) => `${value}%`,
                            color: theme.ONED,
                        },
                        afterDraw: (chart) => {
                            const {ctx, chartArea: {left, top, right, bottom}, scales: {y}} = chart;
                            const y100 = y.getPixelForValue(100);

                            if(y100 >= top && y100 <= bottom) {
                                ctx.save();
                                ctx.beginPath();
                                ctx.moveTo(left, y100);
                                ctx.lineTo(right, y100);
                                ctx.lineWidth = 1;
                                ctx.strokeStyle = theme.Red;
                                ctx.setLineDash([5, 5]);
                                ctx.stroke();
                                ctx.restore();
                            }
                        }
                    },
                    x: {
                        bounds: 'ticks',
                        offset: true,
                        grid: {
                            offset: true
                        },
                        title: {
                            display: false,
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    datalabels: {
                        display: false  //Disable the data of the points
                    },
                    title: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => `${context.dataset.label}: ${context.parsed.y}%`
                        }
                    },
                    backgroundQuadrant: {
                        yValue: 100,
                        color: 'rgba(213, 19, 19, 0.1)'
                    }
                }
            }
        }
    }, [data.nombre, sortedMonths, chartPoints, data.area, theme]);


    if (averagePercent === 0) { //Avoid rendering if the average is zero
        return null;
    }

    return (
        <Col ref = {graphRef} style={{ width: '100%', height: '100%', position: 'relative'}}>
            <Row style={{width: '300px', justifyContent: 'space-between', marginRight: '30px'}}>
                <Text weight="medium" size={15} color={averagePercent > 100 ? theme.Red : theme.ONED}>{data.nombre}</Text>
                <Text weight="medium" size={15} color={averagePercent > 100 ? theme.Red : theme.ONED}>{averagePercent}%</Text>
            </Row>
            {visible && (
                <div className="custom-legend" style={{ position: 'absolute', top: 80, left: 10, border: '1px solid #ccc', padding: '8px 12px', borderRadius: 4, boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: theme.White, zIndex: 1000 }} >
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        <Text size={15} weight='medium' color={theme.ONED}>{data.nombre}</Text>
                        <div style={{height: 12}}></div>
                        <div>
                            <Dropdown theme={theme}  style={{height: 36, width: 180}} value={selectedArea} onChange={handleSelectChange} >
                                {areas.map((area) => (
                                    <MenuItem key={area} value={area} label={area}>{area}</MenuItem>
                                ))}
                            </Dropdown>
                        </div>
                        <div style={{height: 12}}></div>
                        <Dropdown theme={theme}  style={{height: 36, width: 180}} value={selectedLegend} onChange={handleLegendChange} >
                            {[0, 1, 2].map((index) => {
                                const label = index === 0 ? `${index + 1} Turno` : `${index +1} Turnos`;
                                return (
                                    <MenuItem key={index} value={index.toString()}>{label}</MenuItem>
                                )
                            })}
                        </Dropdown>
                    </ul>
                </div>
            )}
            <div style={{ width: '100%', minHeight: 0 }}>
                <Line ref={chartRef} data={chartConfig.data} options={chartConfig.options} />
            </div>
        </Col>
    );
}

export default PlantRecordLineChart;